import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, message, Modal, Popconfirm, Spin, Tabs } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  PlusOutlined,
  SafetyOutlined,
  SearchOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import {
  getAllRoles,
  getMenuModules,
  createMenuModule,
  updateMenuModule,
  deleteMenuModule,
  getMenuActions,
  createMenuAction,
  updateMenuAction,
  deleteMenuAction,
} from '../../../services/adminApi';
import RolePermPanel from './components/RolePermPanel';
import '../../../styles/PermissionsPage.css';

// ── localStorage helpers ──────────────────────────────────────────────────────

const LS_MODULE = 'perm-module-labels';
const LS_ACTION = 'perm-action-labels';

function readLS(key) {
  try { return JSON.parse(localStorage.getItem(key) ?? '{}'); } catch { return {}; }
}
function writeLS(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* ignore */ }
}

// ── Manage modal (modules + actions) ─────────────────────────────────────────

function EditableRow({ label, onSave, onDelete, loading }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);

  function commit() {
    setEditing(false);
    const t = draft.trim();
    if (t && t !== label) onSave(t);
    else setDraft(label);
  }

  return (
    <div className="perm-manage-row">
      {editing ? (
        <input
          autoFocus
          className="perm-inline-input"
          value={draft}
          style={{ flex: 1, minWidth: 0 }}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setEditing(false); setDraft(label); }
          }}
        />
      ) : (
        <span className="perm-manage-row-label">{label}</span>
      )}
      <div className="perm-manage-row-actions">
        <Button
          type="text" size="small" icon={<EditOutlined />}
          onClick={() => { setDraft(label); setEditing(true); }}
          loading={loading}
        />
        <Popconfirm
          title="Delete this item?"
          onConfirm={onDelete}
          okText="Delete" cancelText="Cancel" okType="danger"
        >
          <Button type="text" size="small" icon={<DeleteOutlined />} danger />
        </Popconfirm>
      </div>
    </div>
  );
}

function ManageModal({ open, onClose }) {
  const [tab, setTab] = useState('modules');

  const [modules, setModules] = useState([]);
  const [modLoading, setModLoading] = useState(false);
  const [newModName, setNewModName] = useState('');
  const [savingMod, setSavingMod] = useState(false);

  const [actions, setActions] = useState([]);
  const [actLoading, setActLoading] = useState(false);
  const [newActName, setNewActName] = useState('');
  const [newActKey, setNewActKey] = useState('');
  const [savingAct, setSavingAct] = useState(false);

  useEffect(() => {
    if (!open) return;
    loadModules();
    loadActions();
  }, [open]);

  async function loadModules() {
    setModLoading(true);
    try { setModules(await getMenuModules()); }
    catch { message.error('Failed to load modules'); }
    finally { setModLoading(false); }
  }

  async function loadActions() {
    setActLoading(true);
    try { setActions(await getMenuActions()); }
    catch { message.error('Failed to load actions'); }
    finally { setActLoading(false); }
  }

  async function handleAddModule() {
    const name = newModName.trim();
    if (!name) { message.warning('Enter a module name'); return; }
    setSavingMod(true);
    try {
      const created = await createMenuModule({ menuName: name });
      setModules((prev) => [...prev, created]);
      setNewModName('');
      message.success('Module added');
    } catch { message.error('Failed to add module'); }
    finally { setSavingMod(false); }
  }

  async function handleUpdateModule(id, menuName) {
    try {
      await updateMenuModule(id, { menuName });
      setModules((prev) => prev.map((m) => (m.MENUID === id ? { ...m, MENUNAME: menuName } : m)));
    } catch { message.error('Failed to update module'); }
  }

  async function handleDeleteModule(id) {
    try {
      await deleteMenuModule(id);
      setModules((prev) => prev.filter((m) => m.MENUID !== id));
      message.success('Module deleted');
    } catch { message.error('Failed to delete module'); }
  }

  async function handleAddAction() {
    const name = newActName.trim();
    const key  = newActKey.trim().toLowerCase().replace(/\s+/g, '_');
    if (!name || !key) { message.warning('Enter both name and key'); return; }
    setSavingAct(true);
    try {
      const created = await createMenuAction({ permissionName: name, permissionKey: key });
      setActions((prev) => [...prev, created]);
      setNewActName('');
      setNewActKey('');
      message.success('Action added');
    } catch (err) {
      message.error(err?.message?.includes('409') || err?.status === 409
        ? 'Permission key already exists'
        : 'Failed to add action'
      );
    } finally { setSavingAct(false); }
  }

  async function handleUpdateAction(id, permissionName) {
    try {
      await updateMenuAction(id, { permissionName });
      setActions((prev) => prev.map((a) => (a.permissionId === id ? { ...a, permissionName } : a)));
    } catch { message.error('Failed to update action'); }
  }

  async function handleDeleteAction(id) {
    try {
      await deleteMenuAction(id);
      setActions((prev) => prev.filter((a) => a.permissionId !== id));
      message.success('Action deleted');
    } catch { message.error('Failed to delete action'); }
  }

  const tabItems = [
    {
      key: 'modules',
      label: 'Modules',
      children: (
        <Spin spinning={modLoading}>
          <div className="perm-manage-add-row">
            <Input
              placeholder="Module name"
              value={newModName}
              onChange={(e) => setNewModName(e.target.value)}
              onPressEnter={handleAddModule}
              style={{ flex: 1 }}
              size="small"
            />
            <Button
              type="primary" size="small" icon={<PlusOutlined />}
              loading={savingMod} onClick={handleAddModule}
            >
              Add
            </Button>
          </div>
          <div className="perm-manage-list">
            {modules.length === 0 && !modLoading && (
              <div className="perm-empty" style={{ paddingTop: 24 }}>No modules found</div>
            )}
            {modules.map((m) => (
              <EditableRow
                key={m.MENUID}
                label={m.MENUNAME}
                onSave={(v) => handleUpdateModule(m.MENUID, v)}
                onDelete={() => handleDeleteModule(m.MENUID)}
              />
            ))}
          </div>
        </Spin>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      children: (
        <Spin spinning={actLoading}>
          <div className="perm-manage-add-row">
            <Input
              placeholder="Action name (e.g. Approve)"
              value={newActName}
              onChange={(e) => setNewActName(e.target.value)}
              style={{ flex: 1 }}
              size="small"
            />
            <Input
              placeholder="Key (e.g. approve)"
              value={newActKey}
              onChange={(e) => setNewActKey(e.target.value)}
              onPressEnter={handleAddAction}
              style={{ width: 140 }}
              size="small"
            />
            <Button
              type="primary" size="small" icon={<PlusOutlined />}
              loading={savingAct} onClick={handleAddAction}
            >
              Add
            </Button>
          </div>
          <div className="perm-manage-list">
            {actions.length === 0 && !actLoading && (
              <div className="perm-empty" style={{ paddingTop: 24 }}>No actions found</div>
            )}
            {actions.map((a) => (
              <EditableRow
                key={a.permissionId}
                label={`${a.permissionName} (${a.permissionKey})`}
                onSave={(v) => handleUpdateAction(a.permissionId, v)}
                onDelete={() => handleDeleteAction(a.permissionId)}
              />
            ))}
          </div>
        </Spin>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SettingOutlined style={{ color: 'var(--color-brand)' }} />
          Manage Modules & Actions
        </span>
      }
      width={520}
      footer={null}
      destroyOnClose
    >
      <Tabs activeKey={tab} onChange={setTab} size="small" items={tabItems} />
    </Modal>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PermissionsPage() {
  const [roles,        setRoles]        = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [search,       setSearch]       = useState('');
  const [manageOpen,   setManageOpen]   = useState(false);

  const [moduleLabels, setModuleLabels] = useState(() => readLS(LS_MODULE));
  const [actionLabels, setActionLabels] = useState(() => readLS(LS_ACTION));

  const saveModuleLabel = useCallback((rawName, label) => {
    setModuleLabels((prev) => {
      const next = { ...prev, [rawName]: label };
      writeLS(LS_MODULE, next);
      return next;
    });
  }, []);

  const saveActionLabel = useCallback((key, label) => {
    setActionLabels((prev) => {
      const next = { ...prev, [key]: label };
      writeLS(LS_ACTION, next);
      return next;
    });
  }, []);

  function resetLabels() {
    setModuleLabels({});
    setActionLabels({});
    writeLS(LS_MODULE, {});
    writeLS(LS_ACTION, {});
    message.success('Labels reset to defaults');
  }

  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const { roles: r } = await getAllRoles();
      setRoles(r);
      if (r.length > 0) setSelectedRole(r[0]);
    } catch {
      message.error('Failed to load roles');
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const filteredRoles = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? roles.filter((r) => r.roleName.toLowerCase().includes(q)) : roles;
  }, [roles, search]);

  const hasCustomLabels = Object.keys(moduleLabels).length > 0 || Object.keys(actionLabels).length > 0;

  return (
    <div className="perm-page">

      {/* ── Left: Role list ── */}
      <AppCard
        className="perm-left-card"
        icon={<SafetyOutlined />}
        title="Roles"
        bodyStyle={{ padding: 0 }}
      >
        <div className="perm-role-search">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search roles…"
            allowClear
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Spin spinning={rolesLoading}>
          <div className="perm-role-list">
            {filteredRoles.length === 0 && !rolesLoading && (
              <div className="perm-role-empty">No roles found</div>
            )}
            {filteredRoles.map((role) => {
              const active = selectedRole?.roleId === role.roleId;
              const desc = role.description && role.description !== 'N/A'
                ? role.description
                : role.roleType === 0 ? 'Default role' : 'Custom role';
              return (
                <div
                  key={role.roleId}
                  className={`perm-role-item${active ? ' active' : ''}`}
                  onClick={() => setSelectedRole(role)}
                >
                  <div className="perm-role-item-name">{role.roleName}</div>
                  <div className="perm-role-item-desc">{desc}</div>
                </div>
              );
            })}
          </div>
        </Spin>
      </AppCard>

      {/* ── Right: Module cards ── */}
      <AppCard
        className="perm-right-card"
        icon={<LockOutlined />}
        title="Module Permissions"
        extra={
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {hasCustomLabels && (
              <button className="perm-reset-btn" onClick={resetLabels}>
                Reset labels
              </button>
            )}
            <Button
              size="small"
              icon={<SettingOutlined />}
              onClick={() => setManageOpen(true)}
            >
              Manage
            </Button>
          </span>
        }
        bodyStyle={{ padding: '12px 16px' }}
      >
        {selectedRole ? (
          <RolePermPanel
            key={selectedRole.roleId}
            role={selectedRole}
            moduleLabels={moduleLabels}
            actionLabels={actionLabels}
            onModuleLabelSave={saveModuleLabel}
            onActionLabelSave={saveActionLabel}
          />
        ) : (
          <div className="perm-empty">Select a role from the left to manage its permissions.</div>
        )}
      </AppCard>

      <ManageModal open={manageOpen} onClose={() => setManageOpen(false)} />

    </div>
  );
}
